import { uid } from '@/firebase'
import { toData } from '@/helpers/fs/toData'
import { UserPlan } from '@/helpers/fs/userPlan'
import {
  availableBookingCount,
  formatHumanFullDate,
  FSStripeProSubscription,
  FSUserPlan,
  PlanAvailability,
  TimeStamp,
  UserPlanAvailability,
  UserPlanWithFreeTrial
} from '@WombatTechnology/nitte-time-lib'
import { differenceInDays } from 'date-fns'
import { isEmpty, isNull } from 'es-toolkit/compat'
import { Timestamp } from 'firebase/firestore'
import { defineStore } from 'pinia'

// State
type State = {
  data: FSUserPlan<TimeStamp, FSStripeProSubscription<Timestamp>> | null
  // アップグレードした時にバックエンドとの同期が終わるまで
  // 一時的に表示するプラン
  tentativeUserPlan: 'pro' | null
  subscription: FSStripeProSubscription<Timestamp> | null
}

const HumanizedPlanName: Record<UserPlanWithFreeTrial, string> = {
  freeTrial: 'フリー', // フリートライアルと書く料金がかかると勘違いされることがあったため
  free: 'フリー',
  pro: 'プロ',
  team: 'チーム'
}

export const useUserPlanStore = defineStore('userPlan', {
  state: (): State => {
    return {
      data: null,
      tentativeUserPlan: null,
      subscription: null
    }
  },
  actions: {
    async setup() {
      this.sync('data', UserPlan.doc(uid()!))
      this.syncSubscription()
    },
    async syncSubscription() {
      const data = await toData(UserPlan.doc(uid()!))
      if (data.subscriptionRef) {
        this.sync('subscription', data.subscriptionRef)
      }
    }
  },
  getters: {
    userPlan(state): UserPlanWithFreeTrial | null {
      if (state.tentativeUserPlan) {
        // 裏のデータが更新されるまで表示するユーザープラン
        return state.tentativeUserPlan
      }
      if (isNull(state.data)) {
        return null
      }
      // フリートライアル中
      if (state.data.plan === 'free' && !this.isFreeTrialExpired) {
        return 'freeTrial'
      }
      return state.data.plan
    },
    // 機能の利用可否に関する情報
    availability(_state): UserPlanAvailability | null {
      if (isNull(this.userPlan)) {
        return null
      }
      return PlanAvailability[this.userPlan]
    },
    planName(state): string {
      if (isNull(this.userPlan)) {
        return ''
      }
      return HumanizedPlanName[this.userPlan]
    },
    freeTiralWillExpireInDays(): number | null {
      if (!this.freeTrialExpiredAt) return null
      const days = differenceInDays(this.freeTrialExpiredAt, new Date())
      return days < 0 ? 0 : days
    },
    freeTrialExpiredAt(state): Date | null {
      if (isNull(state.data)) return null
      return state.data.freeTrialExpireAt.toDate()
    },
    isFreeTrialExpired(state): boolean | null {
      if (!this.freeTrialExpiredAt) return null
      return this.freeTrialExpiredAt < new Date()
    },
    bookingCount(state): number {
      return state.data?.bookingCount ?? 0
    },
    availableBookingCountInThisMonth(state): number | 'unlimited' | null {
      if (isNull(state.data)) return null
      return availableBookingCount(state.data)
    },
    availableBookingCountStatus(state): 'ok' | 'warning' | 'error' | null {
      const count = this.availableBookingCountInThisMonth
      if (isNull(count)) {
        return null
      }
      if (count === 'unlimited' || count > 3) {
        return 'ok'
      }
      if (count > 0 && count <= 3) {
        return 'warning'
      }
      return 'error'
    },
    freeTrialExpiredAtFormatted(state): string {
      if (isNull(state.data)) {
        return ''
      }
      return formatHumanFullDate(state.data.freeTrialExpireAt.toDate())
    },
    cancelAtFormatted(state) {
      if (isNull(state.data) || isEmpty(state.subscription?.cancel_at)) {
        return null
      }
      return formatHumanFullDate(state.subscription!.cancel_at!.toDate())
    },
    isProPlanCanceled(state): boolean {
      return !!state.subscription?.cancel_at && state.data?.plan === 'pro'
    },
    isTeamPlan(state): boolean {
      return state.data?.plan === 'team'
    },
    isProPlan(state) {
      return state.data?.plan === 'pro'
    }
  }
})
